<template>
  <div>
    <tool-bar :title="title"></tool-bar>

    <loading :loading="loading"></loading>

    <error-alert-item
      :error-alert-event="errorAlertEvent"
      :network-error="networkError"
      @resetErrorAlertEvent="errorAlertEvent=false">
    </error-alert-item>

    <activity-redirect-item
      :loan-tasks="tasks"
      :current-loan-task="currentTaskName"
      :is-loan-task="true"
      :redirect-activity="redirectActivity"
    ></activity-redirect-item>

    <refresh-item :show="showRefreshItem" @onRefresh="fetchInitialData"></refresh-item>

    <stage-comment-item v-if="!showRefreshItem"></stage-comment-item>

    <div v-if="showUI" class="margin_top_20">
      <div class="margin-left-10 margin-right-10">
        <weex-text class="item-heading">Please Upload Aadhaar Card Images</weex-text>
      </div>


      <file-attach-button
        class="horizontal_center margin_top_40"
        size="medium"
        file-attach-button-id="upload_front_page"
        :show-attach-description="showFieldError('front_page_uploaded')"
        :attach-description="getErrorMsg('front_page_uploaded')"
        text="Front Image"
        :attach-image-des-src="frontPageDocumentAttachImage"
        :enabled-btn-style="attachFileButtonStyle"
        @filesAttached="onFrontPageAttached"
      ></file-attach-button>

      <file-attach-button
        class="horizontal_center margin-top-30"
        size="medium"
        file-attach-button-id="upload_back_page"
        :show-attach-description="showFieldError('back_page_uploaded')"
        :attach-description="getErrorMsg('back_page_uploaded')"
        text="Back Image"
        :attach-image-des-src="backPageDocumentAttachImage"
        :enabled-btn-style="attachFileButtonStyle"
        @filesAttached="onBackPageAttached"
      ></file-attach-button>

      <wxc-button
        text="Next"
        class="margin-top-30 margin-bottom-20 horizontal_center"
        type="blue"
        @wxcButtonClicked="onSubmit"
      ></wxc-button>
    </div>
  </div>
</template>

<script>
import WxcButton from "../../../weex-ui/packages/wxc-button/index";
import ToolBar from "../layout/ToolBar";
import ActivityRedirectItem from "../layout/ActivityRedirectItem";
import ErrorAlertItem from "../layout/ErrorAlertItem";
import Loading from "../layout/Loading";
import RefreshItem from "../layout/RefreshItem";
import StageCommentItem from "../layout/StageCommentItem";
import { Activities } from "../../utils/ActivityNavigator";
import DataSource from "../../data/DataSource";
import LoanScopeDataHolder from "../../data/model/LoanScopeDataHolder";
import LoanTasksActivityNavigator from "../../utils/LoanTasksActivityNavigator";
import FileAttachButton from "../layout/FileAttachButton";
import { ImageResourcesUtil } from "../../utils/ImageResourcesUtil";
import AlertService from "../core/alert/AlertService";
import { EntityType } from "../../utils/EntityType";
import DynamicFieldUtils, { FieldTypes } from "../../utils/DynamicFieldUtils";
import DocumentDetails, { DocumentDescription } from "../../utils/DocumentDetails";

const DOCUMENT_UPLOADED = "Document uploaded successfully";

const ALL_FIELDS_DATA = {
  front_page_uploaded: {
    validation: "",
    fieldType: FieldTypes.BUTTON,
    errorMsg: "<div style='color: red; font-size: 14px; text-align: center'>Please upload Aadhaar Card's front image.</div>",
    optional: false
  },

  back_page_uploaded: {
    validation: "",
    fieldType: FieldTypes.BUTTON,
    errorMsg: "<div style='color: red; font-size: 14px; text-align: center'>Please upload Aadhaar Card's back image.</div>",
    optional: false
  }
};

export default {
  name: "AadhaarCardActivity",
  components: {
    ActivityRedirectItem,
    ToolBar,
    ErrorAlertItem,
    Loading,
    RefreshItem,
    StageCommentItem,
    FileAttachButton,
    WxcButton
  },

  data: () => ({
    title: "Aadhaar Card",
    loading: false,
    errorAlertEvent: false,
    networkError: {},
    showRefreshItem: false,
    showUI: false,
    uploadInstruction: "",
    invalidFields: [],
    aadharImageDetails: {
      front_page_uploaded: false,
      back_page_uploaded: false
    }
  }),

  methods: {
    showToast(message) {
      AlertService.showToast(message);
    },

    showError(error) {
      this.loading = false;
      this.networkError = error;
      this.errorAlertEvent = true;
    },

    showFieldError(fieldKey) {
      return this.invalidFields && this.invalidFields.includes(fieldKey);
    },

    getErrorMsg(fieldKey) {
      return ALL_FIELDS_DATA[fieldKey]["errorMsg"];
    },

    onInputEntered(key, value) {
      this.aadharImageDetails[key] = value;
      this.removeFromInvalidFieldsIfValid(key);
    },

    removeFromInvalidFieldsIfValid(key) {
      this.invalidFields = DynamicFieldUtils.removeFromInvalidFieldsIfValidValue(
        this.invalidFields,
        key,
        this.aadharImageDetails[key],
        ALL_FIELDS_DATA,
        ALL_FIELDS_DATA
      );
    },

    onFrontPageAttached(e) {
      this.onAadhaarPageAttached(e.files[0], DocumentDescription.AADHAAR_FRONT_IMAGE.name);
    },

    onBackPageAttached(e) {
      this.onAadhaarPageAttached(e.files[0], DocumentDescription.AADHAAR_BACK_IMAGE.name);
    },

    onAadhaarPageAttached(file, imageName) {
      const documentDetails = DocumentDetails.getDocumentDetails(imageName);
      this.loading = true;
      DataSource.createDocument(
        EntityType.CLIENTS,
        LoanScopeDataHolder.getClientId(),
        documentDetails.name,
        documentDetails.description,
        documentDetails.directory,
        file
      )
        .then(createDocumentRes => {
          this.loading = false;
          if (createDocumentRes.errorOccurred) {
            this.showError(createDocumentRes.data);
          } else {
            if (imageName === DocumentDescription.AADHAAR_FRONT_IMAGE.name) {
              this.onInputEntered("front_page_uploaded", true);
            } else {
              this.onInputEntered("back_page_uploaded", true);
            }
            this.showToast(DOCUMENT_UPLOADED);
          }
        })
    },

    onSubmit() {
      this.invalidFields = [];
      if (this.checkForValidation()) {
        this.completeCurrentTaskAndGetNextTask();
      }
    },

    checkForValidation() {
      this.invalidFields = DynamicFieldUtils.validateDynamicFieldsForRequiredFields(
        this.aadharImageDetails,
        ALL_FIELDS_DATA,
        ALL_FIELDS_DATA
      );

      return !this.invalidFields || this.invalidFields.length === 0;
    },

    completeCurrentTaskAndGetNextTask() {
      this.loading = true;
      LoanTasksActivityNavigator.getNextTask(this.tasks)
        .then(res => {
          this.loading = false;
          if (res.errorOccurred) {
            this.showError(res.data);
          }
        });
    },

    fetchInitialData() {
      this.aadharImageDetails.front_page_uploaded = false;
      this.aadharImageDetails.back_page_uploaded = false;
      this.showRefreshItem = false;
      this.loading = true;
      DataSource.getDocuments(EntityType.CLIENTS, LoanScopeDataHolder.getClientId())
        .then(documentsResponse => {
          this.loading = false;
          if (documentsResponse.errorOccurred) {
            this.showError(documentsResponse.data);
            this.showRefreshItem = true;
          } else {
            this.showUI = true;
            const documents = documentsResponse.data;

            if (documents && documents.length > 0) {
              documents.forEach(document => {
                if (DocumentDescription.AADHAAR_FRONT_IMAGE.name === document.name) {
                  this.aadharImageDetails.front_page_uploaded = true;
                } else if (DocumentDescription.AADHAAR_BACK_IMAGE.name === document.name) {
                  this.aadharImageDetails.back_page_uploaded = true;
                }
              })
            }
          }
        })
    }
  },

  created() {
    this.fetchInitialData();
  },

  computed: {
    redirectActivity() {
      return Activities.LOAN_DETAILS;
    },

    tasks() {
      return [Activities.AADHAAR_IMAGE];
    },

    currentTaskName() {
      return LoanScopeDataHolder.getCurrentLoanTask()
        ? LoanScopeDataHolder.getCurrentLoanTask().name
        : "";
    },

    frontPageDocumentAttachImage() {
      return this.aadharImageDetails.front_page_uploaded
        ? ImageResourcesUtil.getImageLink(ImageResourcesUtil.TICK_GREEN)
        : ImageResourcesUtil.getImageLink(ImageResourcesUtil.CAMERA_ADD);
    },

    backPageDocumentAttachImage() {
      return this.aadharImageDetails.back_page_uploaded
        ? ImageResourcesUtil.getImageLink(ImageResourcesUtil.TICK_GREEN)
        : ImageResourcesUtil.getImageLink(ImageResourcesUtil.CAMERA_ADD);
    },

    attachFileButtonStyle() {
      return {
        "background-color": "#f87246",
        "border-width": "1px",
        "border-style": "solid",
        "border-color": "gray"
      };
    }
  }
};
</script>

<style scoped src="../../assets/style/base.css"></style>

